/**
 * English copy. Typed against the Spanish catalogue, so a key added there and
 * forgotten here is a compile error rather than a gap the client finds.
 *
 * Open decision (brief §12.1): the English word for "Referente". "Role Model"
 * is used here as the working choice — it keeps the ladder about who sets the
 * standard rather than about how well someone scores. The anchor decks say
 * "Emerging"; whichever wins must be applied in both places at once.
 */
import type { Messages } from "./index.tsx";

export const en: Messages = {
  common: {
    continue: "Continue",
    back: "Back",
    finish: "Finish",
    start: "Start",
    save: "Save",
    edit: "Edit",
    remove: "Remove",
    cancel: "Cancel",
    loading: "Loading…",
    saving: "Sending…",
    retry: "Try again",
    of: "of",
  },

  scale: {
    heading: "The three maturity levels",
    prompt: "Where do you place {name} today?",
    promptSelf: "Where do you place yourself today?",
    step1: "Latent",
    step3: "Consistent",
    step5: "Role Model",
    transition: "In transition",
    transitionFrom: "In transition ({from} → {to})",
    notObserved: "I have not been able to observe this",
    notObservedHint: "— I have no evidence either way",
    lowEnd: "barely shows",
    highEnd: "fully present",
    placeholder: "Tap a number on the scale to answer.",
    yourAnswer: "Your answer",
  },

  pillars: {
    DEBATE: "Debate",
    DECIDE: "Decide",
    DELIVER: "Deliver",
    DEBATE_motto: "creates the best thinking",
    DECIDE_motto: "creates clarity and commitment",
    DELIVER_motto: "creates sustainable impact",
  },

  coaches: {
    title: "Coaches",
    subtitle: "{count} coaches on the programme",
    none: "No coaches yet. Create the first one.",
    new: "Add a coach",
    newTitle: "New coach",
    create: "Create",
    coacheeCount: "{count} assigned",
    createHelp:
      "This only creates the account. Send them their access from the list afterwards.",
    editHelp: "Changing the address changes where the invitation goes.",
    editHelpSignedIn:
      "Changing the address changes where the invitation goes and what they sign in with: this account already has a password, so from now on it is the new address.",
    sendInvitation: "Send invitation",
    resend: "Resend",
    resendConfirm: "Sure? This resets their password",
    notInvited: "not invited",
    invitedOn: "invited {date}",
    hasEntered: "has signed in",
    footer:
      "Admins only: the assignment list is the whole of what a coach can see.",
    footerReadOnly:
      "Read-only: who coaches whom. Creating coaches and assigning leaders is the admin's.",
    field: {
      fullName: "Full name",
      email: "Email",
      phone: "Phone (optional)",
      password: "Password",
    },
    coacheesTitle: "Who they work with",
    coacheesLead:
      "This is all they will see when they sign in: their leaders and their reports.",
    searchPlaceholder: "Search a leader by name or job title…",
    noCoachees: "Nobody assigned. They will sign in to an empty list.",
    assignmentFooter:
      "Each leader has one coach: assigning somebody who already has one hands them over.",
  },

  staff: {
    title: "P&O team",
    subtitle: "{count} client accounts",
    none: "No P&O accounts yet.",
    new: "Add account",
    createHelp:
      "This only creates the account. Send them their access from the list afterwards.",
    footer:
      "Read-only access: they see progress, the leaders, the raters and the coaches. They send no invitations and manage nobody.",
  },

  roleLevels: {
    SELF_LEADER: "Self Leader",
    PEOPLE_LEADER: "People Leader",
    AREA_LEADER: "Area Leader",
  },

  relationships: {
    SELF: "Self-assessment",
    SUPERVISOR: "Supervisor",
    PEER: "Peers",
    DIRECT_REPORT: "Direct reports",
    STAKEHOLDER: "Stakeholders",
  },

  evaluatingGroup: {
    SUPERVISOR: "People you supervise",
    DIRECT_REPORT: "Your supervisor",
    PEER: "Your peers",
    STAKEHOLDER: "Stakeholders",
    SELF: "Your self-assessment",
  },

  survey: {
    greeting: "Hello, {name}.",
    introTitleSelf: "Your own view, first.",
    introLeadSelf:
      "Before you receive feedback from the people you work with, we are asking for your own view. For each behaviour you read the three maturity levels and choose where you stand today.",
    selfPurpose:
      "Your self-assessment is compared with how others see you in your report. That comparison is where the interesting part lives — where you agree and where you do not.",
    selfStepChooseBody: "Pick the level closest to how you work today.",
    selfStepObservedBody:
      "A behaviour you have had no chance to show? Choose “I have not been able to observe this”.",
    doneTitleSelf: "Thank you for your own view.",
    doneTravellingSelf:
      "Once the feedback from others is complete, a Genoma coach will prepare your report and review it before sharing it with you.",
    introTitleIndividual: "A new way to give feedback.",
    introTitleMultiple: "Assess behaviour by behaviour.",
    evaluating: "You are assessing {name}",
    settleIn:
      "Before you start, find somewhere comfortable and unhurried. You will be thinking about specific people, and that goes better without rushing.",
    settleInSelf:
      "Before you start, find somewhere comfortable and unhurried. Looking at yourself honestly goes better without rushing.",
    timeEstimate: "This will take you about {minutes} minutes.",
    timeEstimateHelp:
      "You do not have to do it in one go: we save your progress and you can return through the same link whenever you like.",
    deviceHint:
      "We recommend a laptop or desktop, in Google Chrome. It reads considerably better than on a phone, especially when you are assessing several people at once.",
    deviceHintSelf:
      "We recommend a laptop or desktop, in Google Chrome. The three levels read considerably better than on a phone.",
    introLeadIndividual:
      "For each behaviour you read the three maturity levels and pick the one on the scale that best matches what you have seen. No grades, no judging — just recognising.",
    introLeadMultiple:
      "We are asking for your perspective on {count} leaders you work with. You can do it in stages — we save your progress and you pick up where you left off.",
    stepReadTitle: "Read the three levels.",
    stepReadBody:
      "Latent, Consistent, Role Model — the distinguishing part is in bold.",
    stepChooseTitle: "Choose on the 1–5 scale.",
    stepChooseBody:
      "2 and 4 are intermediate steps, between one level and the next.",
    stepWriteTitle: "One open question at the end.",
    stepWriteBody:
      "After the 12 behaviours we will ask you to write, in your own words, what each person contributes and what would help them grow. It is optional, and it is the part people value most.",
    selfStepWriteBody:
      "At the end we will ask you to write, in your own words, what you contribute and what would help you grow. It is optional, and it is where your coaching conversation starts.",
    stepObservedTitle: "Answer only what you have seen.",
    stepObservedBody:
      "No evidence? Choose “I have not been able to observe this”.",
    anonymityNotice:
      "Your answers are anonymous and combined with others. Nobody sees an individual answer.",
    anonymityNoticeSupervisor:
      "When you assess someone you supervise, your assessment is shown on its own line in their report, labelled as the supervisor’s. Answers from peers, direct reports and stakeholders are anonymous and pooled together.",
    anonymityPurpose: "They help {name} grow; they are not a grade.",
    progress: "{current} / {total}",
    nextGroup: "Next group of people →",
    groupHeading: "{group} · {level} level",
    missingTitle: "You still have {count} answers to give",
    missingBody:
      "We brought you to the first screen you left incomplete. Fill it in and carry on — the rest of your answers are saved.",
    anchorsChangedTitle: "Heads up: the descriptions change",
    anchorsChangedBody:
      "These people are {level}, so the three levels now describe different behaviour. Read them again before you answer.",
    nextBehavior: "Next behaviour →",
    advancing: "Next…",
    nextComments: "Last question →",
    savedAutomatically: "We save your progress automatically.",
    doneTitle: "Thank you for your perspective.",
    doneAnswered: "You answered {answered} behaviours.",
    doneNotObserved: "You marked {count} as not observed.",
    doneTravelling:
      "Your feedback is on its way — anonymously — to {name}’s report. A Genoma coach will review it before it is shared.",
    doneTravellingMany:
      "Your feedback is on its way — anonymously — to each person’s report. A Genoma coach will review them before they are shared.",
    alreadyDoneTitle: "You have already completed this survey.",
    alreadyDoneHelp:
      "There is nothing more to do. If you think you should be able to change an answer, contact whoever invited you.",
    resumeNotice: "Picking up where you left off.",
    linkExpired: "This link has expired.",
    linkExpiredHelp:
      "Ask whoever invited you for a new one and we will resend it.",
  },

  nav: {
    raters: "Raters",
    coaches: "Coaches",
    staff: "P&O team",
    overview: "Overview",
    myReport: "My report",
    participants: "Evaluated",
  },

  roles: {
    ADMIN: "Admin",
    HR: "P&O",
    COACH: "Coach",
    PARTICIPANT: "Participant",
    RATER: "Rater",
  },

  participants: {
    noAccessYet: "no access",
    title: "Evaluated",
    count: "{shown} of {total}",
    searchPlaceholder: "Search by name, role or area…",
    allAreas: "All areas",
    allLevels: "All levels",
    noMatches: "Nobody matches that search.",
    whoAnswered: "Who has answered",
    expandHelp: "Tap a group to see who is in it.",
    foldsIntoGlobal: "into global",
    thresholdHelpFull:
      "A segment is only shown separately when at least {min} people answer; below that its answers are merged into the global average. The tick on the bar marks that minimum.",
    raterSubmitted: "Submitted",
    raterInProgress: "In progress",
    raterPending: "Pending",
    thresholdHelp:
      "The tick on each bar is the minimum of {min} people for that segment to be shown separately.",
    selfAssessment: "Self-assessment",
    outlookBlockedHelp:
      "There are not enough answers yet to produce a report. At least 3 people are needed.",
    outlookAtRiskHelp:
      "These segments have not reached the minimum and still could if reminded: {segments}.",
    outlookGlobalHelp:
      "The report will show a single global figure, with no breakdown.",
    outlookSegmentedHelp:
      "The report will be able to show the segment breakdown.",
    reportNotBuilt: "Not generated",
    reportNotApproved: "Draft · not approved yet",
    reportNotApprovedHelp:
      "The figures are already computed from the answers received. A coach still has to review and approve it.",
    openReport: "Open report",
    reportNotBuiltHelp:
      "The report is produced when the cycle closes, reviewed by a coach, and only then published. That screen is what we are building next.",
  },

  auth: {
    signIn: "Sign in",
    email: "Email",
    password: "Password",
    showPassword: "Show password",
    hidePassword: "Hide password",
    sendLink: "Email me a sign-in link",
    linkSent: "If that address has an account, a sign-in link is on its way.",
    signOut: "Sign out",
    invalidCredentials: "Incorrect email or password.",
  },

  dashboard: {
    seeAllAlerts: "See the other {count}",
    alertsTitle: "Operational alerts",
    alertsLead: "What needs sorting for the reports to come out whole.",
    startedNotSent: "{count} not sent",
    title: "Process progress",
    subtitle: "Operational view · non-confidential",
    closesIn: "closes in {days} days",
    closeDueToday: "closing date is today",
    closeOverdue: "closing date passed {days} days ago",
    evaluated: "Evaluated",
    invitedRaters: "Invited raters",
    invitedRatersHelp: "{pairs} assessments in total",
    completedOf: "{completed} of {invited} have completed their survey",
    reportsApproved: "Reports approved",
    reportsApprovedHelp: "Signed off by a coach after reviewing the figures.",
    responseRate: "Participation",
    ofInvited: "{responded} of {invited} raters have answered",
    selfAssessments: "Self-assessments",
    notStarted: "Not started",
    notStartedHelp: "Surveys nobody has opened yet.",
    byParticipant: "Recent activity",
    seeAll: "See all",
    byRelationship: "By relationship",
    participationTitle: "360 participation",
    legendDone: "received",
    legendShare: "% total",
    responsesReceived: "Assessments received",
    pendingUnit: "assessments",
    totalAssessments: "Total assessments",
    segmentHelp:
      "A segment is only broken out in the report when at least {min} people answer.",
    colName: "Evaluated",
    colParticipation: "Participation",
    colSelf: "Self-assessment",
    colOutlook: "Report today",
    colReport: "Status",
    keyLeader: "key",
    done: "Done",
    pending: "Pending",
    outlookBlocked: "Not enough",
    outlookGlobalOnly: "Global only",
    outlookSegmented: "Segmented",
    notGenerated: "Not generated",
    participation: "Participation",
    alerts: "Operational alerts",
    noAlerts: "No alerts. The process is on track.",
    privacyFooter:
      "Individual answers and qualitative comments are never shown in this view.",
    segmentFolded:
      "Some segments have fewer than 3 responses and are shown inside the global figure for confidentiality.",
  },

  alertSeverity: {
    CRITICAL: "Critical",
    WARNING: "Attention",
    INFO: "Info",
  },

  alerts: {
    reportBlocked:
      "{name}’s report cannot be produced: only {count} person(s) have answered.",
    segmentAtRisk:
      "{name} needs more answers before these can be broken out: {segments}.",
    keyLeaderDelayed:
      "{name} is a key leader and sits at {rate}% participation.",
    selfAssessmentMissing:
      "Everyone has answered about {name}, but they have not sent their own self-assessment.",
    lowParticipation: "{count} evaluated people below 60% participation.",
    raterNotStarted: "{count} raters have not opened their survey.",
    reportAwaitingApproval: "{count} reports are waiting on a coach.",
    surveyReadyToClose: "Everybody answered. The cycle can be closed.",
    closingSoon: "The cycle closes in {count} days.",
    closeDueToday:
      "The cycle closes today and is still open. Close it or move the date.",
    closeOverdue:
      "The closing date passed {count} days ago and the cycle is still open. Close it or move the date.",
  },

  reportStatus: {
    DRAFT: "Draft",
    IN_REVIEW: "In review",
    APPROVED: "Approved",
    PUBLISHED: "Published",
  },

  comments: {
    kicker: "In your words",
    title: "One last question, with no scale",
    titleSelf: "One last question, about you",
    lead: "The numbers say where someone stands. This says why — and it is the part they get the most from.",
    leadSelf:
      "The numbers say where you stand. This says why, and it is where your coaching conversation starts.",
    prompt:
      "What does {name} contribute most as a leader — in their work, with their team, or across their area — and what would help them grow?",
    promptSelf:
      "What do you contribute most as a leader — in your work, with your team, or across your area — and what would help you grow?",
    promptHelp:
      "Use real examples; write as freely and at whatever length you like.",
    placeholder: "What you have seen {name} do…",
    placeholderSelf: "What you have seen in yourself this year…",
    privacyPooled:
      "What you write arrives without your name and mixed in with everyone else who answered: it does not say which group any of it came from. If fewer than three people write something, none of it is shown.",
    privacyAttributed:
      "As their supervisor, your answer is shown as coming from you. It is the only one presented that way, and the person receiving it knows.",
    privacySelf: "Your coach reads this, and it stays in your own report.",
    tooShort: "{count} more word(s) before this counts as an answer.",
    requiredOne: "It has to be answered before the questionnaire can be sent.",
    progress:
      "You have written about {written} of {total} people. All of them are needed to send.",
    stillMissing: "{count} still to write",
  },

  summary: {
    strengths: "What you already do well",
    growth: "What would help you grow",
    divergences: "Where people do not agree",
    divergencesLead:
      "Views that contradict each other are often the most useful part of a 360: they say something looks different depending on where you stand.",
    method:
      "This reading gathers what {count} people wrote about you, and your coach reviewed it. It quotes nobody: the original answers are not shown, because one specific sentence identifies its author far faster than an average does.",
    methodCoach:
      "This reading was drafted by an AI from {count} written answers, and you review it before approving. The leader never sees the original answers: one specific sentence identifies its author far faster than an average does.",
    notGenerated: "Your coach has not prepared this reading yet.",
    notGeneratedCoach:
      "You have not generated the qualitative reading yet. It is written from the open answers, and you can review and edit it before approving.",
    withheld:
      "{count} person(s) wrote something. Neither the text nor a reading of it is shown: at least {min} answers are needed before anything can be said without it being possible to work out who wrote it.",
    notGeneratedReviewer:
      "The qualitative reading has not been generated yet. An admin drafts it and signs it off; once it exists you will see it here, alongside the original answers.",
    methodReviewer:
      "An AI drafted this reading from {count} written answers, and an admin reviews and approves it before it reaches the person it is about. You read it with the original answers beside it. They never see those: one specific sentence identifies its author far faster than an average does.",
    notGeneratedHR:
      "There is no approved qualitative reading for this person yet.",
    methodHR:
      "Written by an AI from the open answers and approved by this person\u2019s coach. The original texts are not shown in P&O: they belong to whoever wrote them and to the coaching conversation.",
    noneWritten: "Nobody wrote anything in the open question.",
    coachOnly: "Coach only",
    provenance: "Written by {model} from {count} answers.",
    edited: "edited by a coach",
    stale: "written with an older prompt version",
    generate: "Generate reading",
    regenerate: "Regenerate",
    working: "Writing…",
    approve: "Approve",
    showSources: "Show the {count} original answers",
    hideSources: "Hide the original answers",
    sourcesNotice:
      "These are the answers exactly as written. Only you see them: they are not in the copy the leader receives. They are here so you can check that the reading says what people said.",
    addTheme: "Add a theme",
    themeLabel: "Title",
    detailLabel: "Text",
    emptySection:
      "This section is empty. You can add a theme or leave it as it is.",
    lastStrength:
      "The last strength cannot be removed: a reading without one cannot be approved.",
    editUnapproves:
      "Saving returns the reading to unapproved: you will have to read it and sign it off again.",
    tooLong: "That runs long. A title takes 80 characters and the text 900.",
    essenceTooShort: "The opening paragraph needs at least 40 characters.",
    status: {
      GENERATED: "Unreviewed draft",
      EDITED: "Edited, not approved",
      APPROVED: "Approved",
    },
  },

  invitations: {
    reportAccess: "Access to their report",
    reportAccessHelp:
      "Sends them a username and password to read their own report. Not the survey link.",
    reportAccessSent:
      "Sent. They will only see their own report, and only once approved.",
    sendReportAccess: "Send access",
    language: "Survey language",
    languageHelp: "The language this person sees it in.",
    languageDefault: "Spanish (default)",
    title: "Invitations",
    email: "Email",
    noEmail: "No address yet",
    addressMovedNeedsResend:
      "Address updated. Links sent to the old one no longer work ({revoked}), and nothing has been sent to the new one yet — send them the invitation. Anything already answered is kept.",
    noEmailReadOnly: "No address yet. P&O can add one so they can be invited.",
    noEmailHelp:
      "The 360 map arrived without addresses. Add one to send their link.",
    none: "This person has no questionnaire in this cycle.",
    selfSurvey: "Their self-assessment",
    othersSurvey: "Assesses {count} person(s)",
    neverSent: "Never sent.",
    sentOn: "Sent {times} time(s) · last on {when}",
    sentTimes: "Sent {times} time(s)",
    completed: "Already completed.",
    send: "Send invitation",
    resend: "Send again",
    sending: "Sending…",
    sentOk: "Sent. The earlier link still works, in case they open that one.",
  },

  invitationRun: {
    title: "Invitations still to send",
    leadIdle:
      "{count} people have never been sent their invitation. They go out one at a time, every {seconds} seconds, so ninety near-identical emails do not arrive together and get filed as spam: about {eta}.",
    leadRunning: "Sending: {sent} of {total}. About {eta} left.",
    leadDone: "Done. {sent} invitations sent.",
    start: "Send {count} invitations",
    confirm: "Yes, send all {count}",
    confirmNote:
      "{count} emails will go out over about {eta}. You can close this tab — the sending carries on server-side. Nobody gets a second invitation.",
    stop: "Stop",
    counts: "{sent} sent · {failed} failed · {skipped} skipped · of {total}",
    sendingTo: "writing to {name}",
    skippedNote:
      "{count} were skipped because somebody had already sent them their link while this was running.",
    minutes: "{count} min",
    underAMinute: "under a minute",
    almostDone: "a moment",
  },
  raters: {
    title: "Invited raters",
    subtitle: "{total} people are assessing somebody",
    pendingInvitations: "{count} still to invite",
    searchPlaceholder: "Search by name, email or area…",
    colPerson: "Person",
    colEmail: "Email",
    new: "Add assessor",
    create: "Create",
    createHelp:
      "This only creates the person. Assign who they assess from their own card afterwards — only then can they be invited.",
    hint: {
      fullName: "Ana Martínez",
      email: "amtz@novonordisk.com",
      jobTitle: "BASAC Procurement Sr Manager",
      area: "F&O, MM&R, LECQ, CA, P&O…",
    },
    field: {
      fullName: "Full name",
      email: "Email",
      jobTitle: "Job title (optional)",
      area: "Area (optional)",
    },
    addPlaceholder: "Add to this list: search by name, job title or code…",
    addBlocked:
      "They have already started their questionnaire, so their list is fixed: their screens were built from these people.",
    addTitle: "Add this person to their list?",
    addBody: "{rater} will also assess {name}.",
    addRelationship: "Relationship to that person",
    addRelationshipHelp:
      "It decides which group their answers join in the report, so it should be the real one.",
    addConfirm: "Add",
    remove: "Remove",
    removeBlocked:
      "They have already started assessing this person: their answers cannot be discarded from here.",
    removeTitle: "Remove this person from their list?",
    removeBody:
      "They will no longer be asked to assess {name}. Their questionnaire gets shorter, and {name}\u2019s report will have one assessor fewer.",
    removeNote:
      "Only possible while nothing has been answered about that person. It is recorded in the audit log.",
    removeConfirm: "Remove",
    colAssigned: "Assesses",
    colProgress: "Progress",
    progressLabel: "{percent}% of the questionnaire",
    colInvitation: "Invitation",
    noEmail: "No email",
    noEmailShort: "No address yet",
    notInvited: "Not invited",
    invited: "Sent ×{times}",
    noMatches: "Nobody matches that search.",
    noJobTitle: "No job title recorded",
    footerCanInvite:
      "Open a person to see who they assess, fix their address and send their link.",
    footerReadOnly:
      "Open a person to see who they assess. Fixing addresses and sending invitations is P&O’s.",
    assessesHeading: "Assesses {count} person(s)",
    assessesLead: "Finished with {done} of {total}.",
    doneButUnsent:
      "Everything is answered, but they have not pressed send: until they do, their answers reach no report.",
    answeredAll: "Complete",
    answeredNoComment: "Comment missing",
    answeredSome: "{answered} of {total}",
    answeredNone: "Not started",
    privacyFooter:
      "This shows how far each person has got, never what they answered. Individual answers are not shown in any view.",
  },

  cycle: {
    picker: "Cycle",
    label: "Surveys",
    statusOpen: "Open",
    statusClosed: "Closed",
    open: "Open surveys",
    close: "Close surveys",
    archived: "Cycle archived",
    openHelp: "{submitted} of {raters} raters have finished",
    closedHelp: "Nobody can answer yet.",
    doOpen: "Yes, open",
    doClose: "Yes, close",
    working: "One moment…",
    confirmOpen:
      "They open for {raters} people, and the 360 map can no longer be re-imported without discarding what they answer.",
    confirmClose:
      "{pending} people have not finished; they will no longer be able to. The {responses} answers already in are kept.",
  },

  report: {
    title: "360° Feedback Report",
    preparedFor: "Prepared for",
    confidential: "Confidential",
    notApprovedYet: "Not approved yet",
    download: "Download PDF",
    downloadHint:
      "Opens the print dialog: choose “Save as PDF” as the destination.",
    kickerAbout: "About this report",
    aboutTitle: "Before you start",
    aboutLead:
      "{name}, this report is a mirror: it brings together how you see yourself and how the people who work with you experience you, across the 12 behaviours of the Debate · Decide · Deliver model. It is not a grade — it is material to grow with. The differences between your view and everyone else’s are the most valuable part of your coaching conversation.",
    rung1: "Barely shows.",
    rung3: "Shows regularly.",
    rung5: "Shows even when it is hard; sets the standard.",
    kickerQuantitative: "Quantitative",
    behaviorsTitle: "Results by behaviour",
    behaviorsLead:
      'Your maturity profile across the 12 behaviours. By default you see your self-assessment against the global figure; change either series, or pick "None" to see one group alone.',
    series: {
      SELF: "Your self-assessment",
      GLOBAL: "Global (everyone who assessed you)",
      SUPERVISOR: "Supervisor",
      PEER: "Peers",
      DIRECT_REPORT: "Direct reports",
      STAKEHOLDER: "Stakeholders",
    },
    seriesA: "Series A",
    seriesB: "Series B",
    seriesNone: "None (show A only)",
    seriesUnavailable: "not available",
    seriesReason: {
      BELOW_MINIMUM: "too few answers to show on its own",
      NO_RESPONSES: "no answers",
      TOO_FEW_SEGMENTS:
        "folded into the global so another group stays unidentifiable",
      MANUAL_OVERRIDE: "folded by hand",
      NOT_DISCLOSED: "not shown separately",
    },
    versus: "vs",
    foldedNotice:
      "These groups are not shown separately because fewer than {min} people answered: {segments}. Their answers are still inside the global average.",
    kickerInterpretation: "Interpretation",
    interpretationTitle: "Strengths and areas to develop",
    strengths: "Your strengths",
    strengthsLead: "Where others place you highest — your base to build on.",
    development: "Areas to develop",
    developmentLead: "The behaviours with the most room ahead.",
    blindSpots: "Blind spots",
    blindSpotsLead:
      "Where you place yourself furthest above how others see you.",
    globalIs: "global {value}.",
    youSeeYourself: "You place yourself at {self} (gap {gap}).",
    currentStep: "Today — {step}:",
    nextStep: "Next rung — {step}:",
    kickerQualitative: "In their words",
    qualitativeTitle: "What people wrote",
    qualitativeLead:
      "The same open question for everyone: what you contribute as a leader, and what would help you grow. Below is what they said, told the way a coach would tell it — not anybody\u2019s sentences.",
    supervisorWords: "What your supervisor wrote",
    supervisorWordsLead:
      "Shown exactly as written and attributed, like their score: there is no anonymity to protect when a single person answers, and the client decided you should be able to read it.",
    ownWords: "What you wrote about yourself",
    noComments: "Nobody has written in this section yet.",
    kickerAction: "Action plan",
    reviewKicker: "Coach review",
    reviewDraft: "This report is a draft",
    reviewDraftHelp:
      "Only coaches and admins can see it. Not P&O, not the leader.",
    reviewReleased: "Approved and shared with {name}",
    reviewReleasedHelp: "P&O can consult it and the leader can now read it.",
    reviewApprove: "Approve report",
    reviewConfirm: "Yes, approve and share",
    reviewConfirmNote:
      "{name} will be able to read their report from that moment, and P&O can consult it. This can be undone.",
    reviewRevert: "Back to draft",
    actionPlanWrite: "Write the plan",
    actionPlanEdit: "Edit the plan",
    actionPlanSaved: "Saved.",
    actionPlanPlaceholder:
      "What to work on, in what order and with what support. The leader reads this.",
    actionPlanDraftNote: "Saved as a draft; it does not publish the report.",
    actionTitle: "Where to start",
    narrativePending: "Your coach has not written this section yet.",
    narrativePendingHelp:
      "The qualitative reading and the action plan are written by a coach from these figures, and reviewed before the report is shared.",
    blocked:
      "There are not enough answers to publish this report yet: at least {min} people are needed.",
    confidentialityNotice:
      "Answers are anonymous: no group with fewer than 3 responses is shown separately.",
    footerOwnership: "This report belongs to you and your coach.",
    self: "Self-assessment",
    others: "Global",
    gap: "Gap",
    notPublished: "Your report is not available yet.",
    notPublishedHelp:
      "A coach is reviewing it. We will let you know as soon as it is ready, at least 3 business days before your session.",
    supervisorNotice:
      "The supervisor line is a single person and is shown that way on purpose; your supervisor knows this.",
  },

  intro: {
    kicker: "360° assessment",
    title: "Before you answer, watch the video to the end.",
    lead: "What you are being asked for, how the three-level scale works, and why it is not a grade. What you see here is exactly what you will find when you open your link.",
    duration: "3 minutes 38 seconds. It plays on a phone.",
    fallback: "Download the video",
  },

  errors: {
    generic: "Something went wrong. Please try again.",
    offline: "You are offline. We will save your progress when you are back.",
    forbidden: "You do not have access to this section.",
    notFound: "We could not find what you were looking for.",
  },

  privacy: {
    link: "Privacy notice",
    title: "How what you answer is protected",
    subtitle: "Privacy and confidentiality notice · 360° assessment",
    back: "← Back",

    summaryTitle: "In one sentence",
    summary:
      "Your assessment does not arrive with your name on it. The person you are assessing never knows who said what, and neither does the company: results are presented as group averages, and only Genoma — the external consultancy running the programme — works with them to prepare each leader’s development.",
    summaryException:
      "There is one exception, made on purpose and flagged before you answer: when you assess someone you are the manager of, your rating is shown separately and identified as the supervisor’s.",

    collectedTitle: "What is collected",
    collectedIdentity:
      "Your name, job title, area, email, language and your relationship to each person you assess, as the organization provided them.",
    collectedAnswers:
      "Your ratings across the 12 behaviors — 1 to 5, or “I have not been able to observe this” — and whatever you write at the end, which is optional.",
    collectedProcess:
      "Whether you opened the questionnaire, whether you submitted it and when. That is operational progress, it is what makes follow-up possible, and it never includes anything you answered.",

    whoTitle: "Who sees what",
    whoLead:
      "This table does not describe what the screen hides: it describes what each person receives. Anything not listed here never travels to that reader.",
    whoColumnReader: "Who",
    whoColumnSees: "What they see",
    whoColumnNever: "What they never see",

    whoYou: "You, the rater",
    whoYouSees: "Your own answers, while you answer and until you submit.",
    whoYouNever: "Nobody else’s answers.",

    whoSubject: "The person being assessed",
    whoSubjectSees:
      "Group averages, with a minimum of 3 people per group, and a synthesis of what was written about them.",
    whoSubjectNever:
      "Any individual answer, any text as it was written, and who makes up each group.",

    whoCoach: "Their coach, at Genoma",
    whoCoachSees:
      "The full report and the sentences people wrote, because a synthesis nobody can check against its source cannot be reviewed.",
    whoCoachNever:
      "Who wrote each sentence. They arrive pooled and shuffled, with no author.",

    whoHr: "The company’s P&O team",
    whoHrSees:
      "Who was asked to assess whom, who answered and who did not, and the email addresses.",
    whoHrNever:
      "No report, no average, no score and nothing written by anyone.",

    whoHrNote:
      "That last row is an explicit decision by the organization: their own team does not read the reports. They run the cycle and see how far it has got, but they do not receive its findings. An internal team is the reader that worries people most when they answer a 360°, and the shortest way to keep the promise is for them not to be a reader at all.",

    rulesTitle: "The rules that hold it up",
    rulesLead:
      "These are not a statement of intent. They are arithmetic, and none of them depends on somebody remembering to apply it.",
    rule1:
      "At least 3 distinct people are needed for a report to exist at all. With fewer, nothing is generated.",
    rule2:
      "At least 3 responses are needed for a group — peers, direct reports, stakeholders — to be shown separately. Below that, it is folded into the global average.",
    rule3:
      "If fewer than one group survives, only the global is shown: a single group beside the global amounts to publishing the rest by elimination.",
    rule4:
      "How many people sit behind each average is never published. Without those counts, no group can be worked out by subtraction.",
    rulesComments:
      "For written text the rule is stricter still: comments are never split by group, even where scores are. Knowing “a peer wrote this” narrows the field from twenty people to six, and with a concrete example that is usually enough.",

    supervisorTitle: "The exception: the supervisor",
    supervisorBody:
      "When you assess someone you are the manager of, your score is shown on its own line, identified as the supervisor’s, and your comment is shown exactly as you wrote it.",
    supervisorWhy:
      "This is the organization’s decision, not an oversight: a supervisor is always one person, so their line is their individual answer and there is no way to anonymise it. Whoever receives the report should know what the person who leads them said.",
    supervisorNotice:
      "You are told before you answer, not after. And if in the same cycle you also assess people you are a peer or stakeholder of, those answers are anonymous and are combined with everyone else’s.",

    commentsTitle: "What you write, and how it arrives",
    commentsStep1: "You write in your own words, at whatever length you want.",
    commentsStep2:
      "The person being assessed does not read your sentence. They read a synthesis of everything written about them, under an explicit ban on quoting, on naming a project, a meeting, a date or a place, on saying which group a view came from, and on saying how many people said something.",
    commentsStep3:
      "A Genoma coach reviews and approves it before anyone reads it. Without that approval, it is not published.",
    commentsStep4: "If fewer than 3 people wrote, none of it is shown.",
    commentsAi:
      "The synthesis is drafted by an Anthropic language model (Claude). It is sent the texts and the first name of the person being assessed; it is not sent the name of whoever wrote, nor their job title, their relationship or their email. The model never receives the piece of information that would let a sentence be attributed, and nothing it produces is published without human review.",

    residualTitle: "What the organization does know",
    residualLead:
      "For honesty, and because a notice that promises too much stops being useful exactly when somebody checks it:",
    residual1:
      "The 360° map was defined by the organization, so it knows who was asked about whom. What it does not know, and cannot work out, is what you answered.",
    residual2:
      "The status of your questionnaire is visible to P&O: whether you opened it, whether you submitted it and when.",
    residual3:
      "In a group of exactly 3 people, anyone who knows the map knows which three people make up that average, though not who said what — and P&O sees no averages on any screen. That is inherent to a 360° with a known map and a threshold of 3.",

    rightsTitle: "Your rights, and who to talk to",
    rightsBody:
      "You can ask for access to your data, correct it, object to its processing or withdraw your consent by writing to {contact}. Withdrawing an answer already submitted means taking it out of the average it forms part of.",
    rightsSecurity:
      "Access to the questionnaire is by a personal link that expires, databases are isolated per organization at the database engine level, and every access to a report is logged.",
    rightsPurpose:
      "This assessment is a development tool. It is not a performance review.",

    contactEmail: "wecare@wearegenoma.com",
    footer: "Data controller: {controller} · Leadership Accelerator programme.",
    controller: "Genoma",
  },
};
